import { APP_INITIALIZER, isDevMode, NgModule } from '@angular/core';
import {
  APP_BASE_HREF,
  DatePipe,
  DecimalPipe,
  TitleCasePipe,
} from '@angular/common';
import {
  HttpClient,
  HttpClientModule,
  HTTP_INTERCEPTORS,
} from '@angular/common/http';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AngularFireModule } from '@angular/fire/compat';
import {
  AngularFireRemoteConfigModule,
  DEFAULTS,
  SETTINGS,
} from '@angular/fire/compat/remote-config';
import { concatMap } from 'rxjs';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { AppRoutingModule } from './app-routing.module';
import { SharedModule } from './modules/shared.module';

import { BrowserStateInterceptor } from './interceptors/browser-state.interceptor';
import { CancelRequestInterceptor } from './interceptors/cancel-request.interceptor';
import { LanguageHandlerService } from './services/language-handler.service';
import { BannerImagePipe } from './pipes/banner-image.pipe';

import { AppComponent } from './app.component';
import { HomeComponent } from './pages/home/home.component';
import { NavbarComponent } from './components/layout/navbar/navbar.component';
import { NavbarLinkComponent } from './components/layout/navbar/navbar-link.component';
import { LoginButtonComponent } from './components/layout/navbar/login-button.component';
import { LanguageSelectionComponent } from './components/utility/language-selection/language-selection.component';
import { PageNotFoundComponent } from './pages/page-not-found/page-not-found.component';
import { FooterComponent } from './components/layout/footer/footer.component';
import { StatisticsSectionComponent } from './components/sections/statistics-section/statistics-section.component';
import { StatisticsCardComponent } from './components/utility/statistics-card/statistics-card.component';
import { BannerSectionComponent } from './components/sections/banner-section/banner-section.component';
import { ErrorDialogComponent } from './components/utility/error-dialog/error-dialog.component';
import { WelcomeDialogComponent } from './components/utility/welcome-dialog/welcome-dialog.component';
import { WhatsNewSectionComponent } from './components/sections/whats-new-section/whats-new-section.component';
import { PopularServicesSectionComponent } from './components/sections/popular-services-section/popular-services-section.component';
import { TrendingServicesSectionComponent } from './components/sections/trending-services-section/trending-services-section.component';
import { CategoriesSectionComponent } from './components/sections/categories-section/categories-section.component';
import { BrandLogoComponent } from './components/layout/navbar/brand-logo.component';
import { ServiceSliderComponent } from './components/utility/service-slider/service-slider.component';
import { StateSectionComponent } from './components/sections/state-section/state-section.component';
import { BenefitsSectionComponent } from './components/sections/benefits-section/benefits-section.component';
import { MySchemeSectionComponent } from './components/sections/my-scheme-section/my-scheme-section.component';
import { CustomerSupportSectionComponent } from './components/sections/customer-support-section/customer-support-section.component';
import { DownloadAppSectionComponent } from './components/sections/download-app-section/download-app-section.component';

import { environment } from 'src/environments/environment';
import { ScrollTopComponent } from './components/utility/scroll-top/scroll-top.component';
import { SuccessComponent } from './components/utility/success/success.component';
import { OpenExternalUrlDialogComponent } from './components/utility/open-external-url-dialog/open-external-url-dialog.component';
import { ResetModalComponent } from './components/utility/reset-modal/reset-modal.component';
import { MessageDialogComponent } from './components/utility/message-dialogue/message-dialogue.component';
import { NgSelectModule } from '@ng-select/ng-select';
import { UserProfileService } from './services/user-profile.service';
import { UpdateAppDialogComponent } from './components/utility/update-app-dialog/update-app-dialog.component';
import { NotifyDialogComponent } from './components/utility/notify-dialog/notify-dialog.component';
import { CloseLightSvgComponent } from './components/layout/svg/close-light-svg.component';
import { DownloadMobileAppComponent } from './components/utility/download-mobile-app/download-mobile-app.component';
import { TollFreeComponent } from './components/utility/toll-free/toll-free.component';
import { ImportantUpdatesComponent } from './components/utility/important-updates/important-updates.component';
import { TimeAgoPipe } from './pipes/notification.pipe';
import { JsonLdComponent } from './components/utility/json-ld/json-ld.component';
import { QuestionnaireComponent } from './components/utility/questionnaire-dialog/questionnaire.component';

export function HttpLoaderFactory(httpClient: HttpClient) {
  return new TranslateHttpLoader(httpClient, './assets/i18n/', '.json');
}

export function setProfileDataFactory(
  userProfileService: UserProfileService,
  langHandlerService: LanguageHandlerService
) {
  // Fetch user profile details.
  return () =>
    langHandlerService
      .changeLanguage('en', false)
      .pipe(
        concatMap(() =>
          langHandlerService
            .setDefaulLanguage()
            .pipe(concatMap(() => userProfileService.initUserProfile()))
        )
      );
}

@NgModule({
  declarations: [
    AppComponent,
    HomeComponent,
    NavbarComponent,
    BrandLogoComponent,
    NavbarLinkComponent,
    LoginButtonComponent,
    LanguageSelectionComponent,
    PageNotFoundComponent,
    FooterComponent,
    StatisticsSectionComponent,
    StatisticsCardComponent,
    SuccessComponent,
    BannerSectionComponent,
    ErrorDialogComponent,
    OpenExternalUrlDialogComponent,
    WhatsNewSectionComponent,
    PopularServicesSectionComponent,
    TrendingServicesSectionComponent,
    CategoriesSectionComponent,
    ServiceSliderComponent,
    StateSectionComponent,
    BenefitsSectionComponent,
    MySchemeSectionComponent,
    CustomerSupportSectionComponent,
    DownloadAppSectionComponent,
    BannerImagePipe,
    ScrollTopComponent,
    WelcomeDialogComponent,
    ResetModalComponent,
    MessageDialogComponent,
    UpdateAppDialogComponent,
    NotifyDialogComponent,
    DownloadMobileAppComponent,
    TollFreeComponent,
    ImportantUpdatesComponent,
    TimeAgoPipe,
    JsonLdComponent,
    QuestionnaireComponent
  ],
  imports: [
    BrowserModule.withServerTransition({ appId: 'serverApp' }),
    AppRoutingModule,
    BrowserAnimationsModule,
    HttpClientModule,
    SharedModule,
    AngularFireModule.initializeApp(environment.firebaseConfig),
    AngularFireRemoteConfigModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    NgSelectModule,
    CloseLightSvgComponent,
  ],
  providers: [
    DatePipe,
    TitleCasePipe,
    { provide: APP_BASE_HREF, useValue: environment.baseUrl },
    {
      provide: APP_INITIALIZER,
      useFactory: setProfileDataFactory,
      deps: [UserProfileService, LanguageHandlerService],
      multi: true,
    },
    { provide: DEFAULTS, useValue: JSON.stringify({ isActive: false }) },
    {
      provide: SETTINGS,
      useFactory: () =>
        isDevMode() ? { minimumFetchIntervalMillis: 10_000 } : {},
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CancelRequestInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: BrowserStateInterceptor,
      multi: true,
    },
    DecimalPipe
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
